import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Info, Share, Globe, Users, MessageSquare, Lock, Search, Volume2, VolumeX, Share2, MoreVertical, X, File, Trash2, Settings } from "lucide-react";
import { MessageBubble } from "./MessageBubble";
import { AnimatedBackground } from "./AnimatedBackground";
import { motion, AnimatePresence } from "framer-motion";
import { MessageInput } from "./MessageInput";
import { useTranslation } from "@/hooks/use-translation";
import { getLanguageByCode } from "@/lib/languages";
import type { ChatMessage, ChatRoomInfo, UserInfo } from "@/lib/types";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ConfirmPopup from "../ui/ConfirmPopup";
import chatBg from "../../assets/images/ChatBg.png"

// ✅ UPDATED: useToast hook path
import { useToast } from "@/hooks/use-toast";
import WavyCircle from "../onboarding/circle";
import { apiUrl } from "@/lib/config";

// All theme colors are now driven by CSS custom properties via the palette system.
// See index.css for palette definitions (neon-pop, tropical-sunset, aurora-borealis, fresh-garden).

// Local-day key (YYYY-MM-DD) for grouping messages by calendar day in the user's timezone.
function dayKeyFor(ts: Date | string): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

function DateSeparator({ timestamp, locale }: { timestamp: Date | string; locale?: string }) {
  const d = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  let label: string;
  if (sameDay(d, today)) {
    label = "Today";
  } else if (sameDay(d, yesterday)) {
    label = "Yesterday";
  } else {
    try {
      label = new Intl.DateTimeFormat(locale || undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
      }).format(d);
    } catch {
      label = d.toDateString();
    }
  }

  return (
    <div className="flex items-center justify-center my-3" role="separator" aria-label={label}>
      <span className="text-[11px] uppercase tracking-wide px-3 py-1 rounded-full bg-black/30 dark:bg-white/10 text-white/80 backdrop-blur-sm">
        {label}
      </span>
    </div>
  );
}

interface ChatWindowProps {
  currentUser: UserInfo | null;
  selectedRoom: ChatRoomInfo | null;
  messages: ChatMessage[];
  onSendMessage: (content: string, type?: string, attachments?: any[]) => void;
  onDeleteMessage?: (messageId: string) => void;
  onBackToChats: () => void;
  onShowRoomInfo: () => void;
  onShareInvite: () => void;
  onOpenSettings?: (section?: string) => void;
  onStartTyping: () => void;
  onStopTyping: () => void;
  typingUsers: string[];
  isMobile: boolean;
  userLanguage: string;
  selectedFiles?: File[];
  onFilesSelected?: (files: File[]) => void;
  onClearFiles?: () => void;
  onRemoveFile?: (index: number) => void;
  messageSearchQuery?: string;
  isMessageSearching?: boolean;
  onRoomDeleted?: () => void;
  autoTranslationEnabled?: boolean;
  onAutoTranslationChange?: (enabled: boolean) => void;
  onReactToMessage?: (messageId: string, emoji: string) => void;
  onUnreactToMessage?: (messageId: string, emoji: string) => void;
  onResendMessage?: (message: ChatMessage) => void;
}

export function ChatWindow({ currentUser, selectedRoom, messages, onSendMessage, onDeleteMessage, onBackToChats, onShowRoomInfo, onShareInvite, onOpenSettings, onStartTyping, onStopTyping, typingUsers, isMobile, userLanguage, selectedFiles = [], onFilesSelected, onClearFiles, onRemoveFile, messageSearchQuery = "", isMessageSearching = false, onRoomDeleted, autoTranslationEnabled = true, onAutoTranslationChange, onReactToMessage, onUnreactToMessage, onResendMessage }: ChatWindowProps) {
  const { toast } = useToast();
  const [isMuted, setIsMuted] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(autoTranslationEnabled);

  // Sync with prop changes
  useEffect(() => {
    setTranslationEnabled(autoTranslationEnabled);
  }, [autoTranslationEnabled]);
  const [showOriginalMessages, setShowOriginalMessages] = useState<Record<string, boolean>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [actionType, setActionType] = useState<"deleteGroup" | "deleteChat" | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ChatMessage[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const highlightedMessageRef = useRef<HTMLDivElement>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [isUserOnline, setIsUserOnline] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const shortName = selectedRoom?.name || "";
  const handleInputFocus = () => {
    setIsInputFocused(true);
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    });
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 40);
  };

  // --- Mute/Unmute Logic ---
  // 1. Load mute status from localStorage when the room changes
  useEffect(() => {
    if (selectedRoom?.roomId) {
      const muteStatus = localStorage.getItem(`chatMuteStatus_${selectedRoom.roomId}`);
      setIsMuted(muteStatus === "true");
    }
  }, [selectedRoom?.roomId]);

  // 2. Scroll to bottom when messages change or files are sent
  useEffect(() => {
    // Use setTimeout to ensure DOM is updated after attachments are rendered
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    };

    // Immediate scroll
    scrollToBottom();

    // Delayed scroll for attachment rendering
    const timeoutId = setTimeout(scrollToBottom, 100);

    return () => clearTimeout(timeoutId);
  }, [messages, selectedFiles]);

  // Additional scroll effect for when attachments are loaded
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.attachments && lastMessage.attachments.length > 0) {
        // Extra delay for media attachments to load
        const timeoutId = setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 300);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [messages]);

  // Load member count when room changes
  const loadMemberCount = () => {
    if (selectedRoom?.roomId) {
      fetch(apiUrl(`/api/rooms/${selectedRoom.roomId}/members`))
        .then(res => res.json())
        .then(members => setMemberCount(members.length))
        .catch(() => setMemberCount(0));
    }
  };

  useEffect(() => {
    loadMemberCount();
  }, [selectedRoom?.roomId]);

  // Track user online status
  useEffect(() => {
    const updateOnlineStatus = () => setIsUserOnline(navigator.onLine);
    const handleActivity = () => setIsUserOnline(true);

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    window.addEventListener('focus', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
    };
  }, []);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // 3. Toggle Mute Function with Toast Notification
  const toggleMute = () => {
    if (!selectedRoom?.roomId) return;

    const newMutedStatus = !isMuted;
    setIsMuted(newMutedStatus);

    // Persist the new status to Local Storage
    localStorage.setItem(`chatMuteStatus_${selectedRoom.roomId}`, newMutedStatus.toString());

    // Show Toast Message
    toast({
      title: newMutedStatus ? "Chat Muted" : "Chat Unmuted",
      description: newMutedStatus ? `Notifications for "${shortName}" are now off.` : `Notifications for "${shortName}" are now on.`,
      variant: "default",
    });
    console.log(" Room:=>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>", selectedRoom);
    console.log(`Room ${selectedRoom.roomId} is now ${newMutedStatus ? "MUTED" : "UNMUTED"}. Status saved to localStorage.`);
  };
  // --- End Mute/Unmute Logic ---

  const handleConfirm = async () => {
    if (!selectedRoom || !currentUser) return;

    try {
      const response = await fetch(apiUrl(`/api/rooms/${selectedRoom.roomId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.uid })
      });

      const result = await response.json();

      if (response.ok) {
        const isLeaving = result.message.includes("Left room");
        toast({
          title: isLeaving ? "Left Room" : (actionType === "deleteGroup" ? "Group Archived" : "Chat Archived"),
          description: result.message,
          variant: "default"
        });
        onRoomDeleted?.(); // Refresh sidebar
        if (!isLeaving) {
          onBackToChats();
        } else {
          // If just leaving, refresh member count and stay in room
          loadMemberCount();
          onBackToChats();
        }
      } else {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete room",
        variant: "destructive"
      });
    }

    setIsPopupOpen(false);
    setActionType(null);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
    setActionType(null);
  };

  const isCreator = selectedRoom?.createdBy === currentUser?.uid;
  const popupConfig =
    actionType === "deleteGroup"
      ? {
        title: isCreator ? "Delete Group?" : "Leave Group?",
        message: isCreator ? "This will archive the group for all members" : "You will leave this group and won't receive new messages",
        confirmText: isCreator ? "Yes, delete group" : "Yes, leave group",
      }
      : actionType === "deleteChat"
        ? {
          title: isCreator ? "Delete Chat?" : "Leave Chat?",
          message: isCreator ? "This will archive the chat for all members" : "You will leave this chat and won't receive new messages",
          confirmText: isCreator ? "Yes, delete chat" : "Yes, leave chat",
        }
        : null;

  const handleToggleOriginal = (messageId: string) => {
    setShowOriginalMessages((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handlePlayAudio = (messageId: string) => {
    console.log("Play audio for message:", messageId);
  };

  const handleDeleteMessage = (messageId: string) => {
    onDeleteMessage?.(messageId);
  };

  // Update search when messageSearchQuery changes
  useEffect(() => {
    if (!messageSearchQuery.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      return;
    }

    const results = messages.filter((message) =>
      message.content.toLowerCase().includes(messageSearchQuery.toLowerCase()) && !message.isDeleted
    );
    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);

    // Scroll to first result
    if (results.length > 0) {
      setTimeout(() => {
        highlightedMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [messageSearchQuery, messages]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      return;
    }

    const results = messages.filter((message) => message.content.toLowerCase().includes(query.toLowerCase()) && !message.isDeleted);
    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);

    // Scroll to first result
    if (results.length > 0) {
      setTimeout(() => {
        highlightedMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  const navigateSearch = (direction: "next" | "prev") => {
    if (searchResults.length === 0) return;

    let newIndex;
    if (direction === "next") {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    }
    setCurrentSearchIndex(newIndex);

    // Scroll to highlighted message
    setTimeout(() => {
      highlightedMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentSearchIndex(-1);
  };

  const clearSearchInput = () => {
    setSearchQuery("");
    setSearchResults([]);
    setCurrentSearchIndex(-1);
    searchInputRef.current?.focus();
  };

  if (!selectedRoom) {
    return (
      <div
        className="flex items-center justify-center text-gray-400
    h-screen  max-w-[1575px] mx-auto w-full sm:h-[90vh] sm:w-[25vw] md:h-[97vh] md:w-[51.5vw] lg:h-[97vh] lg:w-[65vw] xl:h-[97vh] xl:w-[76.5vw]
    px-4 sm:px-6 lg:px-7 dark:bg-[#0f172a]"
      >
        <div className="text-center px-4 max-w-md mx-auto">
          <div className="flex justify-center">
            <WavyCircle />
          </div>
          <p className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">Welcome to Vyzle</p>
          <p className="text-sm sm:text-base mb-4 text-gray-700 dark:text-gray-200">Create or join a secure chat to start messaging.</p>
          <div className="text-center mt-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">Your account will be automatically deactivated after 30 days of inactivity.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col ${isMobile ? "h-[calc(100dvh-64px)]" : "h-[95vh]"} border border-gray-300 dark:border-[rgba(148,163,184,0.4)] rounded-2xl overflow-hidden bg-[var(--chat-bg)]`} style={{
        backgroundImage: `url(${chatBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* On mobile, the page-level `<MobileNav type="chat-room">` in
          chat.tsx already renders a fixed top bar with the room name, back
          button, and overflow menu. We previously rendered a second
          ChatWindow-local header here too — when both were `position: fixed`
          the page-level z-50 bar covered our z-20 one, but after switching
          this one to be in-flow (to fix the iOS-keyboard scroll-away bug),
          both became visible at once. Drop the duplicate. */}
      {!isMobile && (
        <div
          className="relative flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 shadow-sm backdrop-blur-md dark:bg-[#0f172a] bg-[#f8fafc] border-b border-[rgba(148,163,184,0.4)]"

          role="region"
          aria-label="Conversation header"
        >
          {/* Gradient underline */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px]" />
          {isSearchOpen ? (
            <div className="flex items-center justify-between w-full">
              <div
                className="
            flex items-center 
            bg-gray-100 dark:bg-gray-900 
            rounded-full 
            px-3 py-1.5 
            w-full
            focus-within:ring-2 focus-within:ring-[var(--accent)]
            transition-all
          "
              >
                <Search className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search messages..."
                  className="bg-transparent flex-1 text-sm focus:outline-none text-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                />
                <button
                  onClick={closeSearch}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-2"
                  aria-label="Cancel search"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Dropdown button stays visible */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-3 text-muted-foreground hover:bg-[var(--accent)] hover:text-white dark:text-gray-400 transition-colors"
                    aria-label="More conversation options"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={onShowRoomInfo}>
                    <Info className="w-4 h-4 mr-2" />
                    Room Info
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={onShareInvite}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Invite
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={toggleMute}>
                    {isMuted ? (
                      <VolumeX className="w-4 h-4 mr-2" />
                    ) : (
                      <Volume2 className="w-4 h-4 mr-2" />
                    )}
                    {isMuted ? "Unmute" : "Mute"}
                  </DropdownMenuItem>

                  {onOpenSettings && (
                    <DropdownMenuItem onClick={() => onOpenSettings("profile")} className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            // 🟣 NORMAL HEADER MODE
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onBackToChats}
                  className="hover:bg-[var(--accent)]"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>

                <div>
                  <h3 className="font-thin text-base sm:text-lg text-foreground dark:text-gray-400 truncate max-w-[150px] sm:max-w-none">
                    {shortName}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-1 text-xs sm:text-sm text-muted-foreground dark:text-gray-400">
                    {selectedRoom.type === "group" && (
                      <>
                        <div className="w-2 h-2 bg-[var(--online-dot)] rounded-full" />
                        <span className="pl-1">{memberCount} members</span>

                      </>
                    )}
                    <div className="flex items-center space-x-1">
                      <span>
                        {selectedRoom.type === "group"
                          ? ""
                          : "Connected securely"}
                        {/* isUserOnline ? "Online" : "Offline"} */}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 bg-muted/70 dark:bg-muted/30 rounded-lg p-2">
                  <button
                    type="button"
                    onClick={() => onOpenSettings?.("language")}
                    className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer text-left"
                    title="Change language in settings"
                    aria-label="Change language in settings"
                  >
                    <Globe className={`w-4 h-4 ${translationEnabled ? "text-[var(--accent)]" : "text-muted-foreground"
                      }`} />
                    <span className={`text-sm font-medium ${translationEnabled
                      ? "text-[var(--accent)]"
                      : "text-foreground dark:text-gray-400"
                      }`}>
                      Auto Translate ({getLanguageByCode(userLanguage)?.name.toUpperCase() || userLanguage.toUpperCase()})
                    </span>
                  </button>
                  <Switch
                    checked={translationEnabled}
                    onCheckedChange={(checked) => {
                      setTranslationEnabled(checked);
                      onAutoTranslationChange?.(checked);
                    }}
                    className="data-[state=checked]:bg-[var(--accent)]"
                    aria-label="Toggle auto translation"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onShareInvite}
                  className="hover:bg-[var(--bubble-own-to)] text-[var(--bubble-own-to)] hover:text-white"
                >
                  <Share2 className="w-5 h-4 mr-2" />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:bg-[var(--accent)] dark:hover:text-white dark:text-gray-400 transition-colors"
                      aria-label="More conversation options"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#f8fafc]/20  backdrop-blur-[12px]  border border-[rgba(148,163,184,0.2)">
                    <DropdownMenuItem onClick={onShowRoomInfo} className="cursor-pointer">
                      <Info className="w-4 h-4 mr-2" />
                      Room Info
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={onShareInvite} className="cursor-pointer">
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Invite
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={toggleMute} className="cursor-pointer">
                      {isMuted ? (
                        <VolumeX className="w-4 h-4 mr-2" />
                      ) : (
                        <Volume2 className="w-4 h-4 mr-2" />
                      )}
                      {isMuted ? "Unmute" : "Mute"}
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => setIsSearchOpen(true)} className="cursor-pointer">
                      <Search className="w-4 h-4 mr-2" />
                      Search Messages
                    </DropdownMenuItem>

                    {onOpenSettings && (
                      <DropdownMenuItem onClick={() => onOpenSettings("profile")} className="cursor-pointer">
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    {selectedRoom?.type === "direct" && (
                      <DropdownMenuItem
                        onClick={() => {
                          setActionType("deleteChat");
                          setIsPopupOpen(true);
                        }}
                        className="hover:bg-gradient-to-r  hover:text-white cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Exit Chat
                      </DropdownMenuItem>
                    )}

                    {selectedRoom?.type === "group" && (
                      <DropdownMenuItem
                        onClick={() => {
                          setActionType("deleteGroup");
                          setIsPopupOpen(true);
                        }}
                        className="hover:bg-gradient-to-r hover:from-red-600 hover:to-red-900 hover:text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Exit Group
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </div>
      )}


      {selectedFiles.length > 0 ? (
        <div className="flex-1 flex flex-col bg-white/95 dark:bg-transparent backdrop-blur-sm">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200/50 dark:border-gray-700/50">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""} selected
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ready to send</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFiles}
              className="text-gray-600 dark:text-gray-400 hover:text-white dark:hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Preview area */}
          <div className="flex-1 overflow-hidden">
            {selectedFiles.length === 1 ? (
              <div className="flex items-center justify-center w-full h-full p-6">
                {(() => {
                  const file = selectedFiles[0];
                  const isImage = file.type.startsWith("image/");
                  const fileUrl = URL.createObjectURL(file);
                  return (
                    <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 max-w-sm w-full aspect-square">
                      {isImage ? (
                        <img
                          src={fileUrl}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-6">
                          <File className="w-16 h-16 text-blue-600 dark:text-blue-400 mb-4" />
                          <span className="text-sm text-center text-gray-700 dark:text-gray-300 font-medium">
                            {file.name.split(".").pop()?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                        <p className="text-white text-sm truncate font-medium">{file.name}</p>
                        <p className="text-gray-200 text-xs">
                          {(file.size / 1024 / 1024).toFixed(1)}MB
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="p-6 h-full overflow-y-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {selectedFiles.map((file, index) => {
                    const isImage = file.type.startsWith("image/");
                    const fileUrl = URL.createObjectURL(file);
                    return (
                      <div
                        key={index}
                        className="relative group bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 aspect-square"
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveFile?.(index)}
                          className="absolute top-2 right-2 z-10 text-white hover:text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all bg-black/30 backdrop-blur-sm"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        {isImage ? (
                          <img
                            src={fileUrl}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-6">
                            <File className="w-16 h-16 text-blue-600 dark:text-blue-400 mb-4" />
                            <span className="text-sm text-center text-gray-700 dark:text-gray-300 font-medium">
                              {file.name.split(".").pop()?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                          <p className="text-white text-sm truncate font-medium">{file.name}</p>
                          <p className="text-gray-200 text-xs">
                            {(file.size / 1024 / 1024).toFixed(1)}MB
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <ScrollArea
          className="flex-1 pt-3 px-3 sm:px-4 sm:pt-4 overflow-y-auto relative"
          style={{
            WebkitOverflowScrolling: "touch",

          }}
        >
          {/* <AnimatedBackground /> */}
          <div className="space-y-4 relative z-[1]">
            {/* Messages */}
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50 text-gray-800 dark:text-gray-400" />
                  <p className="text-sm sm:text-base text-gray-900 dark:text-white">No messages yet</p>
                  <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">Say hello to start the conversation!</p>
                </div>
              </div>
            ) : (
              <AnimatePresence initial={false}>
                {(() => {
                  const searchTerm = isMessageSearching ? messageSearchQuery : searchQuery;
                  const filteredMessages = messages.filter((message) => {
                    if (message.isDeleted) return false;
                    if (!searchTerm.trim()) return true;
                    return message.content.toLowerCase().includes(searchTerm.toLowerCase());
                  });

                  if (searchTerm.trim() && filteredMessages.length === 0) {
                    return (
                      <div className="flex items-center justify-center h-64 text-muted-foreground">
                        <div className="text-center">
                          <Search className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50 text-gray-800 dark:text-gray-400" />
                          <p className="text-sm sm:text-base text-gray-900 dark:text-white">No messages found</p>
                          <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">Try searching with different words</p>
                        </div>
                      </div>
                    );
                  }

                  const nodes: React.ReactNode[] = [];
                  let lastDayKey: string | null = null;
                  filteredMessages.forEach((message) => {
                    const dayKey = dayKeyFor(message.timestamp);
                    if (dayKey !== lastDayKey) {
                      nodes.push(
                        <DateSeparator
                          key={`date-${dayKey}`}
                          timestamp={message.timestamp}
                          locale={userLanguage}
                        />,
                      );
                      lastDayKey = dayKey;
                    }
                    const isHighlighted = searchResults.length > 0 && currentSearchIndex >= 0 && searchResults[currentSearchIndex]?.messageId === message.messageId;
                    const isOwnMsg = message.senderId === currentUser?.uid;
                    nodes.push(
                      <motion.div
                        key={message.messageId}
                        initial={{ opacity: 0, x: isOwnMsg ? 20 : -20, scale: 0.95 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 300, damping: 24 }}
                      >
                        <div className={`transition-all duration-300 rounded-lg `} ref={isHighlighted ? highlightedMessageRef : null}>
                          <MessageBubble
                            message={message}
                            isOwn={message.senderId === currentUser?.uid}
                            showTranslation={translationEnabled}
                            userLanguage={userLanguage}
                            onToggleOriginal={() => handleToggleOriginal(message.messageId)}
                            onPlayAudio={() => handlePlayAudio(message.messageId)}
                            onDelete={() => handleDeleteMessage(message.messageId)}
                            currentUserId={currentUser?.uid}
                            onReact={onReactToMessage}
                            onUnreact={onUnreactToMessage}
                            onResend={() => onResendMessage?.(message)}
                          />
                        </div>
                      </motion.div>,
                    );
                  });
                  return nodes;
                })()}
              </AnimatePresence>
            )}

            {/* Typing indicator */}
            {typingUsers.length > 0 && (
              <motion.div
                className="flex items-start space-x-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-gradient-to-br from-[var(--avatar-ring-1)] to-[var(--avatar-ring-2)] text-white">
                    <Users className="w-4 h-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="message-bubble-other p-3 animate-typing-wobble" style={{ animation: 'typing-wobble 1.5s ease-in-out infinite' }}>
                  <div className="flex space-x-1.5">
                    <div className="w-2 h-2 rounded-full animate-typing-bounce" style={{ backgroundColor: 'var(--typing-dot)', animation: 'typing-bounce 1.2s ease-in-out infinite' }} />
                    <div className="w-2 h-2 rounded-full animate-typing-bounce" style={{ backgroundColor: 'var(--typing-dot)', animation: 'typing-bounce 1.2s ease-in-out infinite 0.2s' }} />
                    <div className="w-2 h-2 rounded-full animate-typing-bounce" style={{ backgroundColor: 'var(--typing-dot)', animation: 'typing-bounce 1.2s ease-in-out infinite 0.4s' }} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Scroll ref at the end */}
            <div ref={messagesEndRef} data-messages-end />
          </div>

          {/* File Preview Overlay */}
          {selectedFiles.length > 0 && (
            <div className="absolute inset-0 bg-white/95 dark:bg-transparent backdrop-blur-sm z-10 flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200/50 dark:border-gray-700/50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""} selected
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Ready to send</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFiles}
                  className="text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-hidden">
                {selectedFiles.length === 1 ? (
                  <div className="flex items-center justify-center w-full h-full p-6">
                    {(() => {
                      const file = selectedFiles[0];
                      const isImage = file.type.startsWith("image/");
                      const fileUrl = URL.createObjectURL(file);
                      return (
                        <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 max-w-sm w-full aspect-square">
                          {isImage ? (
                            <img
                              src={fileUrl}
                              alt={file.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center p-6">
                              <File className="w-16 h-16 text-blue-600 dark:text-blue-400 mb-4" />
                              <span className="text-sm text-center text-gray-700 dark:text-gray-300 font-medium">
                                {file.name.split(".").pop()?.toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                            <p className="text-white text-sm truncate font-medium">{file.name}</p>
                            <p className="text-gray-200 text-xs">
                              {(file.size / 1024 / 1024).toFixed(1)}MB
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="p-6 h-full overflow-y-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {selectedFiles.map((file, index) => {
                        const isImage = file.type.startsWith("image/");
                        const fileUrl = URL.createObjectURL(file);
                        return (
                          <div
                            key={index}
                            className="relative group bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 aspect-square"
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onRemoveFile?.(index)}
                              className="absolute top-2 right-2 z-10 text-white hover:text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all bg-black/30 backdrop-blur-sm"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            {isImage ? (
                              <img
                                src={fileUrl}
                                alt={file.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center p-6">
                                <File className="w-16 h-16 text-blue-600 dark:text-blue-400 mb-4" />
                                <span className="text-sm text-center text-gray-700 dark:text-gray-300 font-medium">
                                  {file.name.split(".").pop()?.toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                              <p className="text-white text-sm truncate font-medium">{file.name}</p>
                              <p className="text-gray-200 text-xs">
                                {(file.size / 1024 / 1024).toFixed(1)}MB
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </ScrollArea>

      )}

      {/* Message Input (Footer) */}
      <div
        className={`sticky bottom-0 z-10 pt-2 sm:pt-4  transition-all duration-200 ${isInputFocused && isMobile
          ? "pb-5"
          : isMobile
            ? "pb-[env(safe-area-inset-bottom,0px)]"
            : ""
          }`}
      >
        <MessageInput
          onSendMessage={onSendMessage}
          onStartTyping={onStartTyping}
          onStopTyping={onStopTyping}
          userLanguage={userLanguage}
          selectedFiles={selectedFiles}
          onFilesSelected={onFilesSelected}
          onFocus={handleInputFocus}
          onBlur={() => setIsInputFocused(false)}
        />
        {/* <div className="text-center mt-2 dark:bg-[#0F172A] bg-[#f8fafc] w-full">
          <p className="text-[10px] py-1 text-gray-500 dark:text-gray-400">
            Your account will be automatically deactivated after 30 days of inactivity.
          </p>
        </div> */}
      </div>

      {/* Final: Confirmation Popup */}
      {isPopupOpen && popupConfig && <ConfirmPopup isOpen={isPopupOpen} title={popupConfig.title} message={popupConfig.message} confirmText={popupConfig.confirmText} cancelText="Cancel" onConfirm={handleConfirm} onCancel={handleCancel} />}
    </div>
  );
}
